import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Menu,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  AppsOutlined,
  GroupsOutlined,
  LightModeOutlined,
  LogoutOutlined,
  NavigateNext
} from '@mui/icons-material';
import React, { useContext, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { observer } from 'mobx-react';
import { useDependency } from '../../../injections';
import { GeneralStore } from '../../../mesh/stores/GeneralStore';
import { clearAuthStorage, getStoredAzureFlag } from '../../../utils/auth';
import { ThemeContext } from '../../../theme/ThemeProvider';
import { PatchNotesVersionSelect } from './PatchNotesVersionSelect';
import { CockpitDialogTitle } from './CockpitDialogTitle';
import { CockpitDialogActions } from './CockpitDialogActions';

const x1fLogo = '/static/images/mesh/x1F_Logo.png';
const MarkdownRendererWithHighlighting = React.lazy(
  () => import('./MarkdownRenderer')
);

const libraryItems = [
  {
    id: 'chatbot',
    title: 'Chat',
    description: 'Kompetente, effiziente und vertrauenswürdige Unterstützung'
  },
  {
    id: 'company',
    title: 'Company Review',
    description: 'Smarte Kundenanalyse'
  },
  {
    id: 'bid',
    title: 'SmartBid',
    description: 'Tool für intelligente Ausschreibungsanalysen'
  },
  {
    id: 'sesam',
    title: 'Sesam',
    description: 'Intelligente Treasury-Dokumentendatenbank',
    permission: 'SESAM'
  },
  {
    id: 'admin',
    title: 'Admin',
    description: 'Do IT!',
    permission: 'ADMIN'
  }
];

const accountMenuItemSx = {
  minHeight: 48,
  px: 1.5,
  color: 'var(--cockpit-text) !important',
  fontFamily: 'var(--cockpit-font-body)',
  fontSize: 16,
  borderRadius: 'var(--cockpit-radius-pill)',
  '& .MuiSvgIcon-root': { color: 'var(--cockpit-text) !important' },
  '&:hover': {
    color: 'var(--cockpit-text) !important',
    background: 'var(--cockpit-sidebar-active)'
  }
};

const classicSwitchSx = {
  width: 34,
  height: 18,
  p: 0,
  flex: '0 0 auto',
  '&& .MuiSwitch-switchBase': {
    p: 0,
    m: '1px',
    color: 'var(--cockpit-accent-contrast)',
    transitionDuration: '180ms',
    '&:hover': {
      backgroundColor: 'color-mix(in srgb, var(--cockpit-text) 8%, transparent)'
    }
  },
  '&& .MuiSwitch-switchBase.Mui-checked': {
    color: 'var(--cockpit-accent-contrast)',
    transform: 'translateX(16px)',
    '&:hover': {
      backgroundColor: 'color-mix(in srgb, var(--cockpit-text) 8%, transparent)'
    }
  },
  '&& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: 'var(--cockpit-surface-strong)',
    opacity: 1
  },
  '&& .MuiSwitch-track': {
    height: 18,
    borderRadius: 'var(--cockpit-radius-pill)',
    backgroundColor: 'var(--cockpit-surface-strong)',
    opacity: 1,
    boxSizing: 'border-box'
  },
  '&& .MuiSwitch-thumb': {
    width: 16,
    height: 16,
    borderRadius: '50%',
    backgroundColor: 'var(--cockpit-surface-soft)',
    boxShadow: 'none'
  },
  '&& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb': {
    backgroundColor: 'var(--cockpit-accent-contrast)'
  }
};

const PageHeader = observer(function PageHeader() {
  const store = useDependency<GeneralStore>(GeneralStore);
  const { mode, toggleMode } = useContext(ThemeContext);
  const location = useLocation();
  const [accountAnchor, setAccountAnchor] = useState<HTMLElement | null>(null);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [openPatchNotesAfterLibrary, setOpenPatchNotesAfterLibrary] =
    useState(false);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const currentId =
    location.pathname.split('/').filter(Boolean).pop() || 'chatbot';
  const currentTitle =
    libraryItems.find((item) => item.id === currentId)?.title || 'Chat';
  const isCompanyWorkspace = currentId === 'company';
  const visibleLibraryItems = libraryItems.filter(
    (item) =>
      !item.permission ||
      !store.permissionsLoaded ||
      store.hasPermission(item.permission)
  );

  useEffect(() => {
    if (!store.permissionsLoaded) {
      store.getPermissions();
    }
  }, [store, store.permissionsLoaded]);

  const logout = () => {
    clearAuthStorage();
    window.location.href = '/';
  };

  const closePasswordDialog = () => {
    setPasswordOpen(false);
    setOldPassword('');
    setNewPassword('');
    setPasswordError('');
  };

  const handlePasswordChange = () => {
    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>_-]).{8,}$/;
    if (!passwordRegex.test(newPassword)) {
      setPasswordError(
        'Mindestens 8 Zeichen, Groß- & Kleinbuchstaben, Zahl & Sonderzeichen erforderlich.'
      );
      return;
    }
    store.changePS(oldPassword, newPassword);
    closePasswordDialog();
  };

  const openLibrary = async () => {
    setLibraryLoading(true);
    try {
      const requests: Promise<unknown>[] = [
        store.getPermissions(true),
        store.fetchVersion()
      ];
      if (!store.patchNotes && !store.patchNotesLoading) {
        requests.push(store.fetchPatchNotes());
      }
      await Promise.all(requests);
      setLibraryOpen(true);
    } finally {
      setLibraryLoading(false);
    }
  };

  return (
    <>
      <Box
        component="header"
        data-testid="cockpit-header"
        sx={{
          height: 'var(--cockpit-header-height)',
          minHeight: 'var(--cockpit-header-height)',
          flexShrink: 0,
          ml: {
            xs: 0,
            sm: isCompanyWorkspace ? 'var(--cockpit-sidebar-width)' : 0
          },
          width: {
            xs: '100%',
            sm: isCompanyWorkspace
              ? 'calc(100% - var(--cockpit-sidebar-width))'
              : '100%'
          },
          boxSizing: 'border-box',
          px: { xs: 1.5, sm: 2 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          color: 'var(--cockpit-text)',
          backgroundColor: 'var(--cockpit-canvas)',
          borderBottom: '1px solid var(--cockpit-border)',
          transition: 'margin-left 180ms ease',
          position: 'relative',
          zIndex: 40
        }}
      >
        <Box display="flex" alignItems="center" minWidth={0} gap={1.25}>
          <Box
            component="img"
            src={x1fLogo}
            alt="x1F"
            sx={{
              width: 50,
              height: 31,
              pr: 1.25,
              borderRight: '1px solid var(--cockpit-border)',
              objectFit: 'contain',
              filter: 'var(--cockpit-logo-filter)'
            }}
          />
          <Typography
            sx={{
              color: 'inherit',
              textDecoration: 'none',
              fontFamily: 'var(--cockpit-font-heading)',
              fontSize: 20,
              whiteSpace: 'nowrap',
              display: 'inline-flex',
              alignItems: 'center'
            }}
          >
            AI-Cockpit
          </Typography>
          <NavigateNext
            sx={{
              display: { xs: 'none', sm: 'block' },
              color: 'var(--cockpit-header-arrow)',
              fontSize: 20
            }}
          />
          <Typography
            sx={{
              display: { xs: 'none', sm: 'block' },
              ml: 'var(--cockpit-header-current-margin)',
              pl: 'var(--cockpit-header-current-padding-left)',
              pr: 'var(--cockpit-header-current-padding-right)',
              py: 0.35,
              color: 'var(--cockpit-action-text)',
              background: 'var(--cockpit-action-bg)',
              border: '1px solid var(--cockpit-accent)',
              borderRadius: '999px',
              fontFamily: 'var(--cockpit-font-heading)',
              fontSize: 18
            }}
          >
            {currentTitle}
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={{ xs: 0, sm: 0.5 }}>
          <Button
            component="a"
            data-testid="sharepoint-button"
            href="https://x1fgroup.sharepoint.com/sites/x1FCockpit"
            target="_blank"
            rel="noopener noreferrer"
            className="cockpit-secondary-button"
            sx={{ display: { xs: 'none', lg: 'inline-flex' } }}
          >
            SharePoint
          </Button>
          <Button
            component="a"
            data-testid="support-button"
            href="https://helpdesk.x1f.one/services/app/ssp/home"
            target="_blank"
            rel="noopener noreferrer"
            className="cockpit-secondary-button"
            sx={{ display: { xs: 'none', lg: 'inline-flex' } }}
          >
            Support
          </Button>
          <Button
            data-testid="ai-library-button"
            className="cockpit-header-library-button"
            disabled={libraryLoading}
            onClick={() => void openLibrary()}
            startIcon={<AppsOutlined fontSize="small" />}
            sx={{
              display: { xs: 'none', sm: 'inline-flex' },
              mr: 1,
              width: 200,
              height: 34,
              minHeight: 34,
              px: 1.5,
              py: 0,
              minWidth: 0,
              justifyContent: 'flex-start',
              border: '1px solid var(--cockpit-accent)',
              color: 'var(--cockpit-action-text)',
              background: 'var(--cockpit-action-bg)',
              overflow: 'hidden',
              borderRadius: 'var(--cockpit-radius-pill)',
              fontFamily: 'var(--cockpit-font-heading)',
              fontSize: 18,
              letterSpacing: 0,
              '& .MuiButton-startIcon': {
                ml: 0,
                mr: 1.5,
              },
              '& .MuiSvgIcon-root': {
                color: 'inherit'
              },
              '&:hover': {
                borderColor: 'var(--cockpit-accent)',
                color: 'var(--cockpit-accent-contrast)',
                background: 'var(--cockpit-accent)'
              },
              '&.Mui-focusVisible, &:focus-visible': {
                outline: '2px solid var(--cockpit-accent)',
                outlineOffset: 2
              }
            }}
          >
            {libraryLoading ? 'Lädt…' : 'AI Library'}
          </Button>
          <Tooltip title="Benutzerkonto">
            <IconButton
              data-testid="account-menu-button"
              onClick={(event) => setAccountAnchor(event.currentTarget)}
              className="cockpit-secondary-button cockpit-secondary-button--icon-only"
              sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
              aria-label="Benutzerkonto"
            >
              <GroupsOutlined />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Menu
        anchorEl={accountAnchor}
        open={Boolean(accountAnchor)}
        onClose={() => setAccountAnchor(null)}
        PaperProps={{
          sx: {
            mt: 0.75,
            minWidth: 230,
            color: 'var(--cockpit-text)',
            background: 'var(--cockpit-surface)',
            border: '1px solid var(--cockpit-border)',
            borderRadius: 'var(--cockpit-radius-small)',
            boxShadow: 'var(--cockpit-shadow)',
            '& .MuiMenu-list': { p: 1 },
            '&& .MuiMenuItem-root': {
              minHeight: 48,
              px: 1.5,
              color: 'var(--cockpit-text)',
              fontFamily: 'var(--cockpit-font-body)',
              fontSize: 16,
              borderRadius: 'var(--cockpit-radius-pill)',
              '& .MuiSvgIcon-root': { color: 'inherit' },
              '&:hover': {
                color: 'var(--cockpit-text)',
                background: 'var(--cockpit-sidebar-active)'
              }
            }
          }
        }}
      >
        <MenuItem
          data-testid="theme-toggle"
          onClick={() => {
            toggleMode();
            setAccountAnchor(null);
          }}
          sx={{
            ...accountMenuItemSx,
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <LightModeOutlined fontSize="small" />
            Light Theme
          </Box>
          <Switch
            size="small"
            checked={mode === 'light'}
            inputProps={{ 'aria-label': 'Light Theme' }}
            sx={classicSwitchSx}
          />
        </MenuItem>
        <MenuItem
          data-testid="password-change-button"
          disabled={getStoredAzureFlag() === 'true'}
          onClick={() => {
            setAccountAnchor(null);
            setPasswordOpen(true);
          }}
          sx={accountMenuItemSx}
        >
          Passwort ändern
        </MenuItem>
        <MenuItem data-testid="logout-button" onClick={logout} sx={accountMenuItemSx}>
          <LogoutOutlined fontSize="small" sx={{ mr: 1 }} />
          Logout
        </MenuItem>
      </Menu>

      <Dialog
        data-testid="ai-library-dialog"
        open={libraryOpen}
        onClose={() => {
          setOpenPatchNotesAfterLibrary(false);
          setLibraryOpen(false);
        }}
        TransitionProps={{
          onExited: () => {
            if (!openPatchNotesAfterLibrary) return;
            setOpenPatchNotesAfterLibrary(false);
            void store.openPatchNotes();
          }
        }}
        fullWidth
        maxWidth={false}
        PaperProps={{
          sx: {
            width: '100vw',
            maxWidth: 'none',
            position: 'absolute',
            top: 0,
            m: 0,
            p: { xs: 2, md: '46px 48px 48px' },
            minHeight: { md: 307 },
            pointerEvents: openPatchNotesAfterLibrary ? 'none' : 'auto',
            color: 'var(--cockpit-text)',
            background: 'var(--cockpit-library-bg)',
            borderRadius: 0,
            boxShadow: 'var(--cockpit-shadow)'
          }
        }}
      >
        <CockpitDialogTitle
          closeTestId="ai-library-close"
          closeLabel="AI Library schließen"
          onClose={() => setLibraryOpen(false)}
          sx={{ p: 0 }}
          titleSx={{ fontSize: 30 }}
        >
          AI Library
        </CockpitDialogTitle>
        <Box
          sx={{
            mt: 0.5,
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              lg: 'repeat(3, minmax(0, 1fr))',
              xl: 'repeat(4, minmax(0, 1fr))'
            },
            gap: 2.5,
            pb: 1
          }}
        >
          {store.permissionsError ? (
            <Box
              sx={{
                minHeight: 145,
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                color: 'var(--cockpit-text)',
                background: 'var(--cockpit-library-card)',
                borderRadius: 'var(--cockpit-radius)'
              }}
            >
              <Typography
                sx={{
                  color: 'var(--cockpit-accent)',
                  fontFamily: 'var(--cockpit-font-heading)',
                  fontSize: 18,
                  fontWeight: 700
                }}
              >
                Berechtigungen werden neu geladen
              </Typography>
              <Typography sx={{ fontSize: 14 }}>
                Falls Sesam oder Admin fehlen, bitte einmal neu anmelden.
                Technischer Hinweis: {store.permissionsError}
              </Typography>
            </Box>
          ) : null}
          {visibleLibraryItems.map((item) => (
              <Box
                key={item.id}
                data-testid={`ai-library-card-${item.id}`}
                component={Link}
                to={`/${item.id}`}
                onClick={() => setLibraryOpen(false)}
                sx={{
                  minHeight: 112,
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  color: 'var(--cockpit-text)',
                  background: 'var(--cockpit-library-card)',
                  borderRadius: 'var(--cockpit-radius)',
                  textDecoration: 'none',
                  transition:
                    'transform 140ms ease, background-color 140ms ease',
                  '&:focus': {
                    outline: '2px solid var(--cockpit-accent)',
                    outlineOffset: 2
                  },
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    background: 'var(--cockpit-sidebar-active)'
                  }
                }}
              >
                <Typography
                  sx={{
                    color: 'var(--cockpit-accent)',
                    fontFamily: 'var(--cockpit-font-heading)',
                    fontSize: 18,
                    fontWeight: 700
                  }}
                >
                  › {item.title}
                </Typography>
                <Typography sx={{ fontSize: 14 }}>
                  {item.description}
                </Typography>
              </Box>
            ))}
        </Box>
        <Box
          sx={{
            mt: 1.5,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 1
          }}
        >
          <Typography
            data-testid="ai-library-version"
            sx={{
              color: 'var(--cockpit-text-muted)',
              fontFamily: 'var(--cockpit-font-body)',
              fontSize: 12
            }}
          >
            {store.version
              ? `Version: UI ${store.version.frontend} / Plattform ${store.version.backend}`
              : 'Version wird geladen…'}
          </Typography>
          {store.patchNotesList.length > 0 && (
            <Button
              data-testid="ai-library-patchnotes"
              onClick={() => {
                setOpenPatchNotesAfterLibrary(true);
                setLibraryOpen(false);
              }}
              sx={{
                minWidth: 0,
                minHeight: 0,
                p: 0,
                color: 'var(--cockpit-company-link)',
                fontFamily: 'var(--cockpit-font-body)',
                fontSize: 12,
                fontWeight: 400,
                textDecoration: 'underline',
                textTransform: 'none',
                '&:hover': {
                  color: 'var(--cockpit-company-link)',
                  backgroundColor: 'transparent',
                  textDecoration: 'underline'
                }
              }}
            >
              Patchnotes
            </Button>
          )}
        </Box>
        <CockpitDialogActions
          closeTestId="ai-library-close"
          closeLabel="AI Library schließen"
          onClose={() => setLibraryOpen(false)}
          sx={{ px: 0, pb: 0 }}
        />
      </Dialog>

      <Dialog
        data-testid="patchnotes-dialog"
        open={store.patchNotesDialogOpen}
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown
        PaperProps={{
          sx: {
            color: 'var(--cockpit-text)',
            background: 'var(--cockpit-surface)',
            border: '1px solid var(--cockpit-border)',
            borderRadius: 'var(--cockpit-radius)',
            boxShadow: 'var(--cockpit-shadow)'
          }
        }}
      >
        <CockpitDialogTitle
          closeTestId="patchnotes-close-button"
          closeLabel="Patchnotes schließen"
          onClose={() => store.acknowledgePatchNotes()}
        >
          {store.patchNotes?.title ?? 'Patchnotes'}
        </CockpitDialogTitle>
        <DialogContent dividers>
          <PatchNotesVersionSelect
            entries={store.patchNotesList}
            value={store.selectedPatchNotesVersion ?? ''}
            onVersionChange={(version) =>
              void store.selectPatchNotesVersion(version)
            }
          />
          <React.Suspense
            fallback={
              <Typography sx={{ color: 'var(--cockpit-text-muted)' }}>
                Patchnotes werden geladen...
              </Typography>
            }
          >
            <MarkdownRendererWithHighlighting
              markdown={store.patchNotes?.content ?? ''}
            />
          </React.Suspense>
        </DialogContent>
        <CockpitDialogActions
          closeTestId="patchnotes-close-button"
          closeLabel="Patchnotes schließen"
          onClose={() => store.acknowledgePatchNotes()}
          sx={{ px: 3, py: 1.5 }}
        />
      </Dialog>

      <Dialog
        data-testid="password-change-dialog"
        open={passwordOpen}
        onClose={closePasswordDialog}
        PaperProps={{
          sx: {
            color: 'var(--cockpit-text)',
            background: 'var(--cockpit-surface)',
            border: '1px solid var(--cockpit-border)',
            borderRadius: 'var(--cockpit-radius)',
            boxShadow: 'var(--cockpit-shadow)'
          }
        }}
      >
        <CockpitDialogTitle
          closeTestId="password-change-close-button"
          closeLabel="Passwort ändern schließen"
          onClose={closePasswordDialog}
        >
          Passwort ändern
        </CockpitDialogTitle>
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            mt: 1
          }}
        >
          <TextField
            label="Altes Passwort"
            type="password"
            value={oldPassword}
            onChange={(event) => setOldPassword(event.target.value)}
            fullWidth
          />
          <TextField
            label="Neues Passwort"
            type="password"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            error={Boolean(passwordError)}
            helperText={passwordError}
            fullWidth
          />
        </DialogContent>
        <CockpitDialogActions
          closeTestId="password-change-close-button"
          closeLabel="Passwort ändern schließen"
          onClose={closePasswordDialog}
        >
          <Button
            data-testid="password-change-cancel"
            onClick={closePasswordDialog}
            sx={{ color: 'var(--cockpit-text)' }}
          >
            Abbrechen
          </Button>
          <Button
            variant="outlined"
            onClick={handlePasswordChange}
            sx={{
              color: 'var(--cockpit-accent)',
              borderColor: 'var(--cockpit-accent)'
            }}
          >
            Speichern
          </Button>
        </CockpitDialogActions>
      </Dialog>
    </>
  );
});

export default PageHeader;
